import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { createPortal } from 'react-dom';
import { CircleHelp, Gauge, KeyRound, Landmark, Save, Settings2, ShieldCheck, UserRound, X } from 'lucide-react';
import { cpApi } from '../api/api';
import type { VndQrProfile, VndQrProfilePayload, VndQrAutoProfileRequestPayload } from '../types';
import { CustomSelect } from './ui/CustomSelect';
import VndQrProfileSmsBlock from './VndQrProfileSmsBlock';
import VndQrProfileAuditPanel from './VndQrProfileAuditPanel';

type TeamMember = { counterparty_id: number; name: string; login: string };

type FormStrings = {
  name: string;
  bank_name: string;
  bank_account_no: string;
  primary_account_no: string;
  qr_account_mode: 'virtual' | 'primary';
  work_mode: 'auto' | 'manual';
  bank_login: string;
  bank_password: string;
  sepay_api_token: string;
  has_sepay_api_token: boolean;
  payment_provider: 'sepay' | 'casso';
  casso_webhook_secret: string;
  has_casso_webhook_secret: boolean;
  casso_api_key: string;
  has_casso_api_key: boolean;
  responsible_id: string;
  daily_limit: string;
  monthly_limit: string;
  tick_max_requests: string;
  tick_window_minutes: string;
  min_amount: string;
  max_amount: string;
  max_balance_vnd: string;
  max_requests_per_day: string;
  rule_priority: string;
  rule_comment: string;
  account_holder_name: string;
  national_id: string;
  phone_number: string;
  confirm_sms_contact: boolean;
  is_active: boolean;
  is_archived: boolean;
};

/** Допустимые банки при создании/редактировании QR-профиля (значение уходит в API как bank_name). */
export const VND_QR_BANK_OPTIONS = [
  'VCB',
  'BVBANK',
  'BIDV',
  'ACB',
  'STB',
  'VPB',
  'ICB',
] as const;

const VND_QR_AUTO_BANK_OPTIONS = ['BIDV', 'ACB', 'STB', 'VPB', 'ICB', 'VCB'] as const;

const AUTO_BANK_LABELS: Record<(typeof VND_QR_AUTO_BANK_OPTIONS)[number], string> = {
  BIDV: 'BIDV',
  ACB: 'ACB',
  STB: 'Sacombank (STB)',
  VPB: 'VPBank (VPB)',
  ICB: 'VietinBank (VIETIN)',
  VCB: 'Vietcombank (VCB)',
};

function normalizeAutoBankCode(raw: string): string {
  const upper = raw.trim().toUpperCase();
  if (upper.includes('SACOM') || upper === 'STB') return 'STB';
  if (upper.includes('VPBANK') || upper === 'VPB') return 'VPB';
  if (upper.includes('VIETCOM') || upper === 'VCB') return 'VCB';
  if (upper.includes('VIETIN') || upper === 'ICB' || upper === 'CTG') return 'ICB';
  return upper;
}

function isStandardVndBankName(s: string): boolean {
  const code = normalizeAutoBankCode(s);
  return (VND_QR_BANK_OPTIONS as readonly string[]).includes(code);
}

function isAutoBankName(s: string): boolean {
  return (VND_QR_AUTO_BANK_OPTIONS as readonly string[]).includes(
    normalizeAutoBankCode(s) as (typeof VND_QR_AUTO_BANK_OPTIONS)[number],
  );
}

/** Подстановка в форму при редактировании: известный список или сохранённое значение из БД (в т.ч. легаси). */
function bankNameForForm(raw: string): string {
  const t = normalizeAutoBankCode(raw);
  if (isStandardVndBankName(t)) return t;
  if (!t) return VND_QR_BANK_OPTIONS[0];
  return raw.trim();
}

const emptyStrings = (allowManual = false): FormStrings => ({
  name: '',
  bank_name: 'BIDV',
  bank_account_no: '',
  primary_account_no: '',
  qr_account_mode: 'virtual',
  work_mode: allowManual ? 'manual' : 'auto',
  bank_login: '',
  bank_password: '',
  sepay_api_token: '',
  has_sepay_api_token: false,
  payment_provider: 'sepay',
  casso_webhook_secret: '',
  has_casso_webhook_secret: false,
  casso_api_key: '',
  has_casso_api_key: false,
  responsible_id: '',
  daily_limit: '',
  monthly_limit: '',
  tick_max_requests: '',
  tick_window_minutes: '',
  min_amount: '',
  max_amount: '',
  max_balance_vnd: '',
  max_requests_per_day: '',
  rule_priority: '',
  rule_comment: '',
  account_holder_name: '',
  national_id: '',
  phone_number: '',
  confirm_sms_contact: true,
  is_active: false,
  is_archived: false,
});

function numToInput(n: number | undefined | null): string {
  if (n == null || n === 0) return '';
  return String(n);
}

function isBidvBank(name: string) {
  return (name ?? '').toUpperCase().includes('BIDV');
}

function QrAccountSwitch({
  mode,
  onChange,
}: {
  mode: 'virtual' | 'primary';
  onChange: (mode: 'virtual' | 'primary') => void;
}) {
  const { t } = useTranslation();
  const primary = mode === 'primary';
  return (
    <div className="flex flex-col items-center gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{t('qrForm.inQr')}</span>
      <div className="relative isolate inline-grid h-9 w-[11.5rem] grid-cols-2 rounded-full bg-slate-100 p-0.5 shadow-inner dark:bg-white/10">
        <span
          className={`pointer-events-none absolute inset-y-0.5 left-0.5 w-[calc(50%-2px)] rounded-full bg-sky-600 shadow-sm transition-transform duration-200 ease-out ${
            primary ? 'translate-x-full' : 'translate-x-0'
          }`}
        />
        <button
          type="button"
          onClick={() => onChange('virtual')}
          className={`relative z-10 rounded-full text-[12px] font-semibold transition-colors ${
            primary ? 'text-slate-500 dark:text-slate-300' : 'text-white'
          }`}
        >
          {t('qrForm.qrModeVirtualShort')}
        </button>
        <button
          type="button"
          onClick={() => onChange('primary')}
          className={`relative z-10 rounded-full text-[12px] font-semibold transition-colors ${
            primary ? 'text-white' : 'text-slate-500 dark:text-slate-300'
          }`}
        >
          {t('qrForm.qrModePrimaryShort')}
        </button>
      </div>
    </div>
  );
}

function parseNum(s: string): number {
  const t = s.trim().replace(/\s/g, '').replace(',', '.');
  if (!t) return 0;
  const n = Number(t);
  return Number.isFinite(n) ? n : 0;
}

interface Props {
  editProfile: VndQrProfile | null;
  focusSection?: 'sms' | 'audit' | null;
  onClose: () => void;
  onSaved: () => void;
}

function FieldLabel({
  label,
  tooltip,
  labelCls,
}: {
  label: string;
  tooltip?: string;
  labelCls: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="mb-1.5 flex items-baseline gap-1.5">
      <label className={labelCls}>{label}</label>
      {tooltip ? (
        <span className="group relative inline-flex items-baseline">
          <button
            type="button"
            className="inline-flex h-4 w-4 translate-y-[1px] items-center justify-center rounded-full text-slate-400 transition hover:text-slate-700 focus:outline-none dark:hover:text-slate-200"
            tabIndex={-1}
            aria-label={t('qrForm.tooltipAria', { label })}
          >
            <CircleHelp className="h-3.5 w-3.5" />
          </button>
          <span className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 hidden w-64 -translate-x-1/2 rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-[11px] font-normal normal-case tracking-normal text-slate-700 shadow-xl group-hover:block dark:border-white/15 dark:bg-slate-900/95 dark:text-slate-100">
            {tooltip}
          </span>
        </span>
      ) : null}
    </div>
  );
}

type Tone = 'sky' | 'emerald' | 'indigo' | 'violet';

const TONE: Record<Tone, { card: string; iconWrap: string; title: string }> = {
  sky: {
    card: 'border-sky-200/90 bg-gradient-to-br from-sky-50 via-white to-white shadow-[0_1px_0_0_rgba(14,165,233,0.12)] dark:border-sky-400/25 dark:from-sky-500/10 dark:via-white/[0.03] dark:to-transparent dark:shadow-none',
    iconWrap: 'bg-sky-500 text-white',
    title: 'text-sky-950 dark:text-sky-100',
  },
  emerald: {
    card: 'border-emerald-200/90 bg-gradient-to-br from-emerald-50 via-white to-white shadow-[0_1px_0_0_rgba(16,185,129,0.14)] dark:border-emerald-400/25 dark:from-emerald-500/10 dark:via-white/[0.03] dark:to-transparent dark:shadow-none',
    iconWrap: 'bg-emerald-500 text-white',
    title: 'text-emerald-950 dark:text-emerald-100',
  },
  indigo: {
    card: 'border-indigo-200/90 bg-gradient-to-br from-indigo-50 via-white to-white shadow-[0_1px_0_0_rgba(99,102,241,0.14)] dark:border-indigo-400/25 dark:from-indigo-500/10 dark:via-white/[0.03] dark:to-transparent dark:shadow-none',
    iconWrap: 'bg-indigo-500 text-white',
    title: 'text-indigo-950 dark:text-indigo-100',
  },
  violet: {
    card: 'border-violet-200/90 bg-gradient-to-br from-violet-50 via-white to-white shadow-[0_1px_0_0_rgba(139,92,246,0.14)] dark:border-violet-400/25 dark:from-violet-500/10 dark:via-white/[0.03] dark:to-transparent dark:shadow-none',
    iconWrap: 'bg-violet-500 text-white',
    title: 'text-violet-950 dark:text-violet-100',
  },
};

function Section({
  title,
  hint,
  tone,
  icon,
  children,
}: {
  title: string;
  hint?: string;
  tone: Tone;
  icon: ReactNode;
  children: ReactNode;
}) {
  const s = TONE[tone];
  return (
    <section className={`rounded-xl border p-4 ${s.card}`}>
      <div className="mb-3 flex items-start gap-2.5">
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg shadow-sm ${s.iconWrap}`}>
          {icon}
        </div>
        <div className="min-w-0 pt-0.5">
          <h4 className={`text-sm font-semibold tracking-tight ${s.title}`}>{title}</h4>
          {hint ? <p className="mt-0.5 text-[11px] leading-snug text-slate-500 dark:text-slate-400">{hint}</p> : null}
        </div>
      </div>
      {children}
    </section>
  );
}

function Toggle({
  label,
  on,
  onToggle,
  tone = 'emerald',
}: {
  label: string;
  on: boolean;
  onToggle: () => void;
  tone?: 'emerald' | 'rose';
}) {
  const onColor = tone === 'rose' ? 'bg-rose-500' : 'bg-emerald-500';
  return (
    <div className="flex min-h-[4.25rem] items-center justify-between rounded-xl border border-slate-200 bg-white px-3.5 py-3 shadow-sm dark:border-white/10 dark:bg-white/[0.04] dark:shadow-none">
      <span className="pr-3 text-sm font-medium leading-snug text-slate-700 dark:text-slate-200">{label}</span>
      <button
        type="button"
        onClick={onToggle}
        className={`relative inline-flex h-7 w-11 shrink-0 items-center rounded-full transition-colors ${
          on ? onColor : 'bg-slate-300 dark:bg-slate-600'
        }`}
        aria-pressed={on}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
            on ? 'translate-x-[22px]' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}

export default function VndQrProfileFormWidget({ editProfile, focusSection, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [responsibleId, setResponsibleId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormStrings>(() => emptyStrings(false));
  const [editingId, setEditingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [manualIntakeEnabled, setManualIntakeEnabled] = useState(false);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const isNewAutoProfile = form.work_mode === 'auto' && !editingId;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [data, profile] = await Promise.all([
          cpApi.getVndTeam().catch(() => null),
          cpApi.getProfile().catch(() => null),
        ]);
        if (cancelled) return;
        const list = (data?.members ?? []).map((m) => ({
          counterparty_id: m.counterparty_id,
          name: m.name,
          login: m.login,
        }));
        setMembers(list);
        setResponsibleId(data?.team?.responsible_counterparty_id ?? null);
        setManualIntakeEnabled(Boolean(profile?.manual_intake_enabled));
      } catch {
        if (!cancelled) {
          setMembers([]);
          setResponsibleId(null);
          setManualIntakeEnabled(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const responsibleOptions = useMemo(() => {
    const opts = [{ value: '', label: t('qrForm.notChosen') }];
    const resp = responsibleId;
    for (const m of members) {
      const tag = resp != null && m.counterparty_id === resp ? t('qrForm.responsibleTag') : '';
      opts.push({
        value: String(m.counterparty_id),
        label: `${m.name} (${m.login})${tag}`,
      });
    }
    return opts;
  }, [members, responsibleId, t]);

  const bankSelectOptions = useMemo(() => {
    const availableBanks = form.work_mode === 'auto' ? VND_QR_AUTO_BANK_OPTIONS : VND_QR_BANK_OPTIONS;
    const base = availableBanks.map((b) => ({
      value: b,
      label:
        form.work_mode === 'auto'
          ? AUTO_BANK_LABELS[b as (typeof VND_QR_AUTO_BANK_OPTIONS)[number]] ?? b
          : b,
    }));
    const v = form.bank_name.trim();
    if (v && !isStandardVndBankName(v)) {
      return [{ value: v, label: v }, ...base];
    }
    return base;
  }, [form.bank_name, form.work_mode]);

  const workModeOptions = useMemo(() => {
    const auto = { value: 'auto' as const, label: t('qrForm.modeAuto') };
    const manual = { value: 'manual' as const, label: t('qrForm.modeManual') };
    const showManual = manualIntakeEnabled || form.work_mode === 'manual';
    return showManual ? [manual, auto] : [auto];
  }, [t, manualIntakeEnabled, form.work_mode]);

  const providerOptions = useMemo(
    () => [
      { value: 'sepay', label: t('qrForm.providerSepay') },
      { value: 'casso', label: t('qrForm.providerCasso') },
    ],
    [t],
  );

  const autoModeAllowed = isAutoBankName(form.bank_name);

  useEffect(() => {
    if (!editProfile) return;
    setEditingId(editProfile.id);
    setForm({
      name: editProfile.name,
      bank_name: bankNameForForm(editProfile.bank_name),
      bank_account_no: editProfile.bank_account_no ?? '',
      primary_account_no: editProfile.primary_account_no ?? '',
      qr_account_mode: editProfile.qr_account_mode === 'primary' ? 'primary' : 'virtual',
      work_mode: editProfile.work_mode ?? 'manual',
      bank_login: editProfile.bank_login ?? '',
      bank_password: editProfile.bank_password ?? '',
      sepay_api_token: '',
      has_sepay_api_token: !!editProfile.has_sepay_api_token,
      payment_provider: editProfile.payment_provider === 'casso' ? 'casso' : 'sepay',
      casso_webhook_secret: '',
      has_casso_webhook_secret: !!editProfile.has_casso_webhook_secret,
      casso_api_key: '',
      has_casso_api_key: !!editProfile.has_casso_api_key,
      responsible_id: editProfile.responsible_counterparty_id ? String(editProfile.responsible_counterparty_id) : '',
      daily_limit: numToInput(editProfile.daily_limit),
      monthly_limit: numToInput(editProfile.monthly_limit),
      tick_max_requests: numToInput(editProfile.tick_max_requests),
      tick_window_minutes: numToInput(editProfile.tick_window_minutes),
      min_amount: numToInput(editProfile.min_amount),
      max_amount: numToInput(editProfile.max_amount),
      max_balance_vnd: numToInput(editProfile.max_balance_vnd),
      max_requests_per_day: numToInput(editProfile.max_requests_per_day),
      rule_priority: numToInput(editProfile.rule_priority),
      rule_comment: editProfile.rule_comment ?? '',
      account_holder_name: editProfile.account_holder_name ?? '',
      national_id: editProfile.national_id ?? '',
      phone_number: editProfile.phone_number ?? '',
      confirm_sms_contact: true,
      is_active: editProfile.is_active,
      is_archived: Boolean(editProfile.is_archived || editProfile.deleted_at),
    });
    setError(null);
  }, [editProfile]);

  useEffect(() => {
    if (editProfile) return;
    setEditingId(null);
    setForm(emptyStrings(manualIntakeEnabled));
    setError(null);
  }, [editProfile, manualIntakeEnabled]);

  useEffect(() => {
    if (!focusSection || !editingId) return;
    const id = focusSection === 'audit' ? 'vnd-qr-audit-panel' : 'vnd-qr-sms-block';
    const timer = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 180);
    return () => window.clearTimeout(timer);
  }, [focusSection, editingId]);

  useEffect(() => {
    if (form.work_mode === 'auto' && !isAutoBankName(form.bank_name)) {
      setForm((prev) => ({ ...prev, bank_name: 'BIDV' }));
    }
  }, [form.work_mode, form.bank_name]);

  const buildPayload = (): VndQrProfilePayload => {
    const ruleP = parseNum(form.rule_priority);
    return {
      name: form.name.trim(),
      bank_name: form.bank_name.trim(),
      bank_account_no: form.bank_account_no.trim() || undefined,
      primary_account_no: isBidvBank(form.bank_name) ? form.primary_account_no.trim() : '',
      qr_account_mode: isBidvBank(form.bank_name) && form.qr_account_mode === 'primary' ? 'primary' : 'virtual',
      work_mode: form.work_mode,
      bank_login: undefined,
      bank_password: undefined,
      ...(form.sepay_api_token.trim() ? { sepay_api_token: form.sepay_api_token.trim() } : {}),
      ...(form.work_mode === 'auto'
        ? {
            payment_provider: form.payment_provider,
            ...(form.payment_provider === 'casso'
              ? {
                  ...(form.casso_webhook_secret.trim() ? { casso_webhook_secret: form.casso_webhook_secret.trim() } : {}),
                  ...(form.casso_api_key.trim() ? { casso_api_key: form.casso_api_key.trim() } : {}),
                }
              : {}),
          }
        : {}),
      responsible_counterparty_id: form.responsible_id ? parseInt(form.responsible_id, 10) : undefined,
      daily_limit: parseNum(form.daily_limit),
      monthly_limit: parseNum(form.monthly_limit),
      tick_max_requests: parseNum(form.tick_max_requests),
      tick_window_minutes: parseNum(form.tick_window_minutes),
      min_amount: parseNum(form.min_amount),
      max_amount: parseNum(form.max_amount),
      max_balance_vnd: parseNum(form.max_balance_vnd),
      max_requests_per_day: parseNum(form.max_requests_per_day),
      rule_priority: ruleP > 0 ? ruleP : 100,
      rule_comment: form.rule_comment.trim() || undefined,
      is_active: form.is_active,
      is_archived: form.is_archived,
      account_holder_name: form.account_holder_name.trim() || undefined,
      national_id: form.national_id.trim() || undefined,
      phone_number: form.phone_number.trim() || undefined,
    };
  };

  const buildAutoRequestPayload = (): VndQrAutoProfileRequestPayload => {
    const base = buildPayload();
    return {
      ...base,
      source_profile_id: editingId ?? undefined,
      work_mode: 'auto',
      account_holder_name: form.account_holder_name.trim(),
      national_id: form.national_id.trim(),
      phone_number: form.phone_number.trim(),
      confirm_sms_contact: form.confirm_sms_contact,
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.bank_name.trim() || !form.bank_account_no.trim()) {
      setError(t('qrForm.validationMain'));
      return;
    }
    if (isBidvBank(form.bank_name) && form.qr_account_mode === 'primary' && !form.primary_account_no.trim()) {
      setError(t('qrForm.validationPrimaryAccount'));
      return;
    }
    if (form.work_mode === 'auto' && !autoModeAllowed) {
      setError(t('qrForm.validationAutoBanks'));
      return;
    }
    if (form.work_mode !== 'auto' && !manualIntakeEnabled && !editingId) {
      setError(t('qrForm.manualIntakeDisabled'));
      return;
    }
    const payload = buildPayload();
    const minA = parseNum(form.min_amount);
    const maxA = parseNum(form.max_amount);
    if (maxA > 0 && minA > maxA) {
      setError(t('qrForm.validationMinMax'));
      return;
    }
    if (isNewAutoProfile) {
      if (!form.account_holder_name.trim() || !form.national_id.trim() || !form.phone_number.trim()) {
        setError(t('qrForm.validationPersonalRequired'));
        return;
      }
      if (!form.confirm_sms_contact) {
        setError(t('qrForm.validationSmsConfirm'));
        return;
      }
    }
    setSaving(true);
    setError(null);
    try {
      if (isNewAutoProfile) {
        await cpApi.createVndQrAutoProfileRequest(buildAutoRequestPayload());
        window.dispatchEvent(new CustomEvent('showToast', {
          detail: { message: t('qrForm.autoRequestSuccess'), type: 'success' },
        }));
      } else if (editingId) {
        await cpApi.updateVndQrProfile(editingId, payload);
      } else {
        await cpApi.createVndQrProfile(payload);
      }
      onSaved();
      onClose();
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 409) {
        const d = e.response.data as { error?: string; message?: string } | string | undefined;
        if (d && typeof d !== 'string' && d.error === 'enable_locked') {
          setError(d.message || t('qrProfiles.enableLocked'));
          return;
        }
        const fromApi = typeof d === 'string' ? d.trim() : '';
        setError(fromApi || t('qrForm.duplicateBankAccount'));
        return;
      }
      if (axios.isAxiosError(e) && e.response?.status === 403) {
        const d = e.response.data as { error?: string } | undefined;
        if (d?.error === 'manual_intake_disabled') {
          setError(t('qrForm.manualIntakeDisabled'));
          return;
        }
      }
      setError(t('qrForm.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const field =
    'w-full px-3 py-2.5 text-sm rounded-xl border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 shadow-inner focus:outline-none focus:border-emerald-500/60 focus:ring-2 focus:ring-emerald-500/20 dark:border-white/10 dark:bg-white/[0.06] dark:text-white dark:placeholder:text-slate-500 dark:focus:border-emerald-400/40 transition-[border-color,box-shadow]';
  const labelCls = 'block text-[11px] font-medium uppercase tracking-wide text-slate-500 mb-1.5 dark:text-slate-400';
  const selectTrigger = '!py-2.5 !text-sm rounded-xl';

  return createPortal(
    <>
      <div className="fixed inset-0 z-[220] bg-slate-900/40 backdrop-blur-[2px] dark:bg-slate-950/70" />
      <div className="fixed inset-3 z-[221] flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-[#f4f7fb] shadow-2xl dark:border-white/10 dark:bg-[#0b1220] sm:inset-4 lg:inset-5">
        <div className="flex shrink-0 items-center gap-3 border-b border-slate-200 bg-white px-4 py-3 dark:border-white/10 dark:bg-white/[0.04] sm:px-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-sm">
            <Settings2 className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-base font-semibold tracking-tight text-slate-900 dark:text-white">
              {editingId ? t('qrForm.editTitle', { id: editingId }) : t('qrForm.newTitle')}
            </div>
            <div className="mt-0.5 text-[12px] text-slate-500 dark:text-slate-400">
              {editingId ? t('qrForm.editSubtitle') : t('qrForm.subtitle')}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 disabled:opacity-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-white"
            aria-label={t('common.cancel')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-3 overflow-x-visible overflow-y-auto overscroll-contain px-4 py-4 sm:px-5">
            {error ? (
              <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200">{error}</div>
            ) : null}

            <div className="grid grid-cols-1 gap-3 xl:grid-cols-2 xl:items-start">
              <div className="flex min-w-0 flex-col gap-3">
                <Section title={t('qrForm.sectionMain')} hint={t('qrForm.sectionMainHint')} tone="sky" icon={<Landmark className="h-4 w-4" />}>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <label className={labelCls}>{t('qrForm.name')}</label>
                      <input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} className={field} placeholder={t('qrForm.namePlaceholder')} />
                    </div>
                    <div>
                      <label className={labelCls}>{t('qrForm.workMode')}</label>
                      <CustomSelect
                        value={form.work_mode}
                        onChange={(v) => {
                          const nextMode = v as 'auto' | 'manual';
                          if (nextMode === 'manual' && !manualIntakeEnabled && form.work_mode !== 'manual') return;
                          setError(null);
                          setForm((p) => ({
                            ...p,
                            work_mode: nextMode,
                            bank_name: nextMode === 'auto' ? 'BIDV' : p.bank_name || VND_QR_BANK_OPTIONS[0],
                          }));
                        }}
                        options={workModeOptions}
                        className="w-full"
                        menuClassName="max-h-48"
                        triggerClassName={selectTrigger}
                        variant="surface"
                      />
                      {!manualIntakeEnabled && form.work_mode !== 'manual' ? (
                        <p className="mt-1.5 text-[11px] text-slate-500 dark:text-slate-400">{t('qrForm.manualIntakeDisabledHint')}</p>
                      ) : null}
                    </div>
                    <div>
                      <FieldLabel label={t('qrForm.bank')} labelCls={labelCls} />
                      <CustomSelect
                        value={form.bank_name}
                        onChange={(v) =>
                          setForm((p) => ({
                            ...p,
                            bank_name: v,
                            qr_account_mode: isBidvBank(v) ? p.qr_account_mode : 'virtual',
                          }))
                        }
                        options={bankSelectOptions}
                        className="w-full"
                        menuClassName="max-h-56"
                        triggerClassName={selectTrigger}
                        variant="surface"
                        noTranslate
                      />
                      {form.work_mode === 'auto' ? (
                        <p className="mt-1.5 text-[11px] text-slate-500 dark:text-slate-400">{t('qrForm.autoBanksHint')}</p>
                      ) : null}
                      {!autoModeAllowed ? (
                        <p className="mt-1.5 text-[11px] text-amber-800 dark:text-amber-200/90">{t('qrForm.validationAutoBanks')}</p>
                      ) : null}
                    </div>
                    {isBidvBank(form.bank_name) ? (
                      <div className="sm:col-span-2 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto_1fr] sm:items-end">
                        <div>
                          <label className={labelCls}>{t('qrForm.virtualAccount')}</label>
                          <input
                            value={form.bank_account_no}
                            onChange={(e) => setForm((p) => ({ ...p, bank_account_no: e.target.value }))}
                            className={`${field} font-mono`}
                            placeholder={t('qrForm.accountPlaceholder')}
                          />
                        </div>
                        <QrAccountSwitch
                          mode={form.qr_account_mode === 'primary' ? 'primary' : 'virtual'}
                          onChange={(mode) => setForm((p) => ({ ...p, qr_account_mode: mode }))}
                        />
                        <div>
                          <label className={labelCls}>{t('qrForm.primaryAccount')}</label>
                          <input
                            value={form.primary_account_no}
                            onChange={(e) => setForm((p) => ({ ...p, primary_account_no: e.target.value }))}
                            className={`${field} font-mono`}
                            placeholder={t('qrForm.primaryAccountPlaceholder')}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="sm:col-span-2">
                        <label className={labelCls}>{t('qrForm.accountNo')}</label>
                        <input
                          value={form.bank_account_no}
                          onChange={(e) => setForm((p) => ({ ...p, bank_account_no: e.target.value }))}
                          className={`${field} font-mono`}
                          placeholder={t('qrForm.accountPlaceholder')}
                        />
                      </div>
                    )}
                    <div className="sm:col-span-2">
                      <label className={labelCls}>{t('qrForm.responsible')}</label>
                      <CustomSelect
                        value={form.responsible_id}
                        onChange={(v) => setForm((p) => ({ ...p, responsible_id: v }))}
                        options={responsibleOptions}
                        className="w-full"
                        menuClassName="max-h-56"
                        triggerClassName={selectTrigger}
                        variant="surface"
                      />
                      {members.length === 0 ? (
                        <p className="mt-1.5 text-[11px] text-amber-800 dark:text-amber-200/90">{t('qrForm.membersLoadHint')}</p>
                      ) : null}
                    </div>
                    <div className={`grid gap-3 sm:col-span-2 ${editingId ? 'grid-cols-2' : 'grid-cols-1'}`}>
                      <Toggle
                        label={t('qrForm.profileActive')}
                        on={form.is_active}
                        onToggle={() =>
                          setForm((p) => ({
                            ...p,
                            is_active: !p.is_active,
                            ...(!p.is_active ? { is_archived: false } : {}),
                          }))
                        }
                      />
                      {editingId ? (
                        <Toggle
                          label={t('qrForm.archive')}
                          tone="rose"
                          on={form.is_archived}
                          onToggle={() =>
                            setForm((p) => {
                              const nextArchived = !p.is_archived;
                              return {
                                ...p,
                                is_archived: nextArchived,
                                ...(nextArchived ? { is_active: false } : {}),
                              };
                            })
                          }
                        />
                      ) : null}
                    </div>
                  </div>
                </Section>

                {form.work_mode === 'auto' ? (
                  <Section title={t('qrForm.sectionHolder')} hint={t('qrForm.sectionHolderHint')} tone="violet" icon={<UserRound className="h-4 w-4" />}>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="sm:col-span-2">
                        <label className={labelCls}>{t('qrForm.accountHolderName')}</label>
                        <input
                          value={form.account_holder_name}
                          onChange={(e) => setForm((p) => ({ ...p, account_holder_name: e.target.value }))}
                          className={field}
                          placeholder={t('qrForm.accountHolderNamePlaceholder')}
                        />
                      </div>
                      <div>
                        <label className={labelCls}>{t('qrForm.nationalId')}</label>
                        <input
                          value={form.national_id}
                          onChange={(e) => setForm((p) => ({ ...p, national_id: e.target.value }))}
                          className={`${field} font-mono`}
                          placeholder={t('qrForm.nationalIdPlaceholder')}
                        />
                      </div>
                      <div>
                        <label className={labelCls}>{t('qrForm.phoneNumber')}</label>
                        <input
                          value={form.phone_number}
                          onChange={(e) => setForm((p) => ({ ...p, phone_number: e.target.value }))}
                          className={`${field} font-mono`}
                          placeholder={t('qrForm.phoneNumberPlaceholder')}
                        />
                      </div>
                      {isNewAutoProfile ? (
                        <div className="sm:col-span-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-xs text-amber-900 dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-100">
                          <p>{t('qrForm.smsNotice')}</p>
                          <label className="mt-2 flex items-start gap-2 text-[12px] text-amber-800 dark:text-amber-200">
                            <input
                              type="checkbox"
                              checked={form.confirm_sms_contact}
                              onChange={(e) => setForm((p) => ({ ...p, confirm_sms_contact: e.target.checked }))}
                              className="mt-0.5"
                            />
                            <span>{t('qrForm.smsConsent')}</span>
                          </label>
                        </div>
                      ) : null}
                    </div>
                  </Section>
                ) : null}
              </div>

              <div className="flex min-w-0 flex-col gap-3">
                <Section
                  title={t('qrForm.sectionProvider')}
                  hint={form.work_mode === 'auto' ? t('qrForm.sectionProviderHintAuto') : t('qrForm.sectionProviderHintManual')}
                  tone="emerald"
                  icon={<KeyRound className="h-4 w-4" />}
                >
                  {form.work_mode === 'auto' ? (
                    <div className="grid grid-cols-1 gap-3">
                      <div>
                        <label className={labelCls}>{t('qrForm.paymentProvider')}</label>
                        <CustomSelect
                          value={form.payment_provider}
                          onChange={(v) => setForm((p) => ({ ...p, payment_provider: v === 'casso' ? 'casso' : 'sepay' }))}
                          options={providerOptions}
                          className="w-full"
                          menuClassName="max-h-48"
                          triggerClassName={selectTrigger}
                          variant="surface"
                        />
                      </div>
                      {form.payment_provider === 'casso' ? (
                        <>
                          <div>
                            <label className={labelCls}>{t('qrForm.cassoWebhookSecret')}</label>
                            <input
                              type="password"
                              autoComplete="new-password"
                              value={form.casso_webhook_secret}
                              onChange={(e) => setForm((p) => ({ ...p, casso_webhook_secret: e.target.value }))}
                              className={`${field} font-mono`}
                              placeholder={editingId && form.has_casso_webhook_secret ? t('qrForm.cassoKeep') : t('qrForm.cassoWebhookSecretPlaceholder')}
                            />
                            {editingId && form.has_casso_webhook_secret ? (
                              <p className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
                                <ShieldCheck className="h-3.5 w-3.5" />
                                {t('qrForm.cassoWebhookSecretSaved')}
                              </p>
                            ) : (
                              <p className="mt-1.5 text-[11px] text-slate-500 dark:text-slate-400">{t('qrForm.cassoWebhookSecretHint')}</p>
                            )}
                          </div>
                          <div>
                            <label className={labelCls}>{t('qrForm.cassoApiKey')}</label>
                            <input
                              type="password"
                              autoComplete="new-password"
                              value={form.casso_api_key}
                              onChange={(e) => setForm((p) => ({ ...p, casso_api_key: e.target.value }))}
                              className={`${field} font-mono`}
                              placeholder={editingId && form.has_casso_api_key ? t('qrForm.cassoKeep') : t('qrForm.cassoApiKeyPlaceholder')}
                            />
                            {editingId && form.has_casso_api_key ? (
                              <p className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
                                <ShieldCheck className="h-3.5 w-3.5" />
                                {t('qrForm.cassoApiKeySaved')}
                              </p>
                            ) : (
                              <p className="mt-1.5 text-[11px] text-slate-500 dark:text-slate-400">{t('qrForm.cassoApiKeyHint')}</p>
                            )}
                          </div>
                        </>
                      ) : (
                        <div>
                          <label className={labelCls}>{t('qrForm.sepayApiToken')}</label>
                          <input
                            type="password"
                            autoComplete="new-password"
                            value={form.sepay_api_token}
                            onChange={(e) => setForm((p) => ({ ...p, sepay_api_token: e.target.value }))}
                            className={`${field} font-mono`}
                            placeholder={editingId && form.has_sepay_api_token ? t('qrForm.sepayApiTokenKeep') : t('qrForm.sepayApiTokenPlaceholder')}
                          />
                          {editingId && form.has_sepay_api_token ? (
                            <p className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
                              <ShieldCheck className="h-3.5 w-3.5" />
                              {t('qrForm.sepayApiTokenSaved')}
                            </p>
                          ) : (
                            <p className="mt-1.5 text-[11px] text-slate-500 dark:text-slate-400">{t('qrForm.sepayApiTokenHint')}</p>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex min-h-[11rem] flex-col items-center justify-center rounded-lg border border-dashed border-emerald-200 bg-emerald-50/50 px-4 py-6 text-center dark:border-emerald-500/25 dark:bg-white/[0.03]">
                      <KeyRound className="mb-2 h-8 w-8 text-emerald-500 dark:text-emerald-400/80" />
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-200">{t('qrForm.apiNotNeeded')}</p>
                      <p className="mt-1 max-w-xs text-[12px] leading-snug text-slate-500 dark:text-slate-400">{t('qrForm.apiNotNeededHint')}</p>
                    </div>
                  )}
                </Section>

                <div id="vnd-qr-sms-block">
                  <VndQrProfileSmsBlock profileId={editingId} bankName={form.bank_name} />
                </div>
                <div id="vnd-qr-audit-panel">
                  <VndQrProfileAuditPanel profileId={editingId} />
                </div>
              </div>
            </div>

            <Section title={t('qrForm.sectionLimits')} hint={t('qrForm.sectionLimitsHint')} tone="indigo" icon={<Gauge className="h-4 w-4" />}>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <label className={labelCls}>{t('qrForm.dailyVnd')}</label>
                  <input inputMode="decimal" value={form.daily_limit} onChange={(e) => setForm((p) => ({ ...p, daily_limit: e.target.value }))} className={field} placeholder="—" />
                </div>
                <div>
                  <label className={labelCls}>{t('qrForm.monthlyVnd')}</label>
                  <input inputMode="decimal" value={form.monthly_limit} onChange={(e) => setForm((p) => ({ ...p, monthly_limit: e.target.value }))} className={field} placeholder="—" />
                </div>
                <div>
                  <FieldLabel label={t('qrForm.minVnd')} tooltip={t('qrForm.minVndTip')} labelCls={labelCls} />
                  <input inputMode="decimal" value={form.min_amount} onChange={(e) => setForm((p) => ({ ...p, min_amount: e.target.value }))} className={field} placeholder="—" />
                </div>
                <div>
                  <FieldLabel label={t('qrForm.maxVnd')} tooltip={t('qrForm.maxVndTip')} labelCls={labelCls} />
                  <input inputMode="decimal" value={form.max_amount} onChange={(e) => setForm((p) => ({ ...p, max_amount: e.target.value }))} className={field} placeholder="—" />
                </div>
                <div>
                  <FieldLabel label={t('qrForm.maxBalance')} tooltip={t('qrForm.maxBalanceTip')} labelCls={labelCls} />
                  <input inputMode="decimal" value={form.max_balance_vnd} onChange={(e) => setForm((p) => ({ ...p, max_balance_vnd: e.target.value }))} className={field} placeholder="0" />
                </div>
                <div>
                  <FieldLabel label={t('qrForm.tickMax')} tooltip={t('qrForm.tickMaxTip')} labelCls={labelCls} />
                  <input inputMode="numeric" value={form.tick_max_requests} onChange={(e) => setForm((p) => ({ ...p, tick_max_requests: e.target.value }))} className={field} placeholder="—" />
                </div>
                <div>
                  <FieldLabel label={t('qrForm.tickWindow')} tooltip={t('qrForm.tickWindowTip')} labelCls={labelCls} />
                  <input inputMode="numeric" value={form.tick_window_minutes} onChange={(e) => setForm((p) => ({ ...p, tick_window_minutes: e.target.value }))} className={field} placeholder="—" />
                </div>
                <div>
                  <label className={labelCls}>{t('qrForm.maxPerDay')}</label>
                  <input inputMode="numeric" value={form.max_requests_per_day} onChange={(e) => setForm((p) => ({ ...p, max_requests_per_day: e.target.value }))} className={field} placeholder="—" />
                </div>
                <div>
                  <FieldLabel label={t('qrForm.rulePriority')} tooltip={t('qrForm.rulePriorityTip')} labelCls={labelCls} />
                  <input inputMode="numeric" value={form.rule_priority} onChange={(e) => setForm((p) => ({ ...p, rule_priority: e.target.value }))} className={field} placeholder={t('qrForm.priorityPlaceholder')} />
                </div>
                <div className="sm:col-span-2 lg:col-span-3">
                  <label className={labelCls}>{t('qrForm.ruleComment')}</label>
                  <textarea
                    value={form.rule_comment}
                    onChange={(e) => setForm((p) => ({ ...p, rule_comment: e.target.value }))}
                    rows={3}
                    className={`${field} min-h-[4rem] resize-y`}
                    placeholder={t('qrForm.commentPlaceholder')}
                  />
                </div>
              </div>
            </Section>
          </div>

          <div className="flex shrink-0 flex-wrap gap-2 border-t border-slate-200 bg-white px-4 py-3 dark:border-white/10 dark:bg-white/[0.04] sm:px-5">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-400 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {saving
                ? t('common.saving')
                : isNewAutoProfile
                  ? t('qrForm.submitAutoRequest')
                  : editingId
                    ? t('qrForm.saveProfile')
                    : t('qrForm.createProfile')}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:opacity-50 dark:border-white/15 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
            >
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </div>
    </>,
    document.body,
  );
}
